<template>
  <div class="letter-templates-container">
    <!-- Letters Grid -->
    <div v-if="loading" class="text-center py-12">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
      <div class="mt-4 text-gray-500 font-medium">Fetching letter templates...</div>
    </div>

    <div
      v-else-if="!filteredLetters.length"
      class="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300 mx-4"
    >
      <v-icon size="80" color="grey lighten-2">mdi-file-document-edit-outline</v-icon>
      <h3 class="mt-4 text-xl font-semibold text-gray-700">No Letter Templates</h3>
      <p class="text-gray-500 mt-2 max-w-sm mx-auto">
        Create reusable letter templates that can be attached to your notifications.
      </p>
      <v-btn color="primary" @click="openCreateDialog" outlined class="mt-4 normal-case px-6">
        Create Your First Letter
      </v-btn>
    </div>

    <div v-else class="grid gap-6 md:grid-cols-2 lg:grid-cols-3 px-4">
      <v-card
        v-for="letter in filteredLetters"
        :key="letter._id"
        class="letter-card overflow-hidden border border-gray-200 rounded-md elevation-0"
        hover
      >
        <div class="pa-4">
          <div class="d-flex justify-space-between align-center mb-3">
            <div class="d-flex align-center">
              <div
                :class="[getTypeStyle(letter.type).bg, 'p-2 rounded-lg mr-3 d-flex align-center']"
              >
                <v-icon :color="getTypeStyle(letter.type).color" size="24">{{
                  getTypeStyle(letter.type).icon
                }}</v-icon>
              </div>
              <div class="d-flex flex-column">
                <h3 class="text-md font-bold text-gray-800 leading-tight pr-2">
                  {{ letter.name }}
                </h3>
              </div>
            </div>
            <div class="d-flex align-center">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    color="primary"
                    class="mr-0"
                    @click="previewLetter(letter)"
                  >
                    <v-icon small>mdi-eye-outline</v-icon>
                  </v-btn>
                </template>
                <span>Preview</span>
              </v-tooltip>
              <v-menu offset-y left transition="slide-y-transition">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small v-bind="attrs" v-on="on" color="grey darken-1">
                    <v-icon small>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list dense class="rounded-lg py-1 shadow-lg border border-gray-100">
                  <v-list-item @click="editLetter(letter)">
                    <v-list-item-icon class="mr-3"
                      ><v-icon small color="primary">mdi-pencil-outline</v-icon></v-list-item-icon
                    >
                    <v-list-item-title class="font-medium text-sm">Edit Template</v-list-item-title>
                  </v-list-item>
                  <v-divider class="my-1"></v-divider>
                  <v-list-item @click="confirmDelete(letter)">
                    <v-list-item-icon class="mr-3"
                      ><v-icon small color="error">mdi-delete-outline</v-icon></v-list-item-icon
                    >
                    <v-list-item-title class="text-red-600 font-medium text-sm"
                      >Delete</v-list-item-title
                    >
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </div>

          <div class="d-flex align-center gap-2 mb-3">
            <v-chip
              x-small
              label
              v-if="letter.type"
              :color="getTypeStyle(letter.type).bg"
              :class="`${getTypeStyle(letter.type).color}--text font-bold px-2`"
              class="elevation-0"
            >
              <v-icon left x-small :color="getTypeStyle(letter.type).color">{{
                getTypeStyle(letter.type).icon
              }}</v-icon>
              {{ letter.type || '-' }}
            </v-chip>
            <v-chip v-if="letter.module" x-small label class="text-blue-grey darken-3 font-bold">
              {{ formatModuleName(letter.module) }}
            </v-chip>
            <v-chip
              v-if="letter.type === 'ATTACHMENT' && letter.attachmentType"
              x-small
              label
              color="purple darken-1"
              class="white--text font-bold px-2 elevation-0"
            >
              <v-icon left x-small color="white">mdi-file-document-outline</v-icon>
              {{ letter.attachmentType }} FORMAT
            </v-chip>
          </div>

          <div
            class="d-flex align-center text-[10px] text-gray-400 mb-3 font-medium uppercase tracking-tighter"
          >
            <v-icon size="10" class="mr-1">mdi-clock-outline</v-icon>
            Updated: {{ formatDate(letter.updatedAt || letter.createdAt) }}
          </div>

          <v-divider class="mb-3"></v-divider>

          <div class="d-flex align-center justify-space-between">
            <span
              class="text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded uppercase"
              >Template</span
            >
            <div class="d-flex align-center text-[10px] text-gray-400">
              <v-icon size="10" class="mr-1">mdi-calendar-check</v-icon>
              {{ formatDate(letter.createdAt, true) }}
            </div>
          </div>
        </div>
      </v-card>
    </div>

    <!-- Preview Dialog -->
    <v-dialog v-model="previewDialog" max-width="850px">
      <v-card class="p-4 overflow-hidden">
        <v-card-title class="py-4 px-6 d-flex justify-space-between border-b border-gray-100">
          <div class="d-flex flex-column">
            <h3 class="text-lg font-bold text-gray-800">
              {{ selectedLetter?.name || selectedLetter?.letterName }}
            </h3>
          </div>
          <v-btn icon @click="previewDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-6 bg-gray-50 overflow-y-auto" style="max-height: 70vh">
          <div
            class="bg-white p-8 rounded-lg shadow-sm border border-gray-100 preview-content"
            v-html="selectedLetter?.content || selectedLetter?.letterContent"
          ></div>
        </v-card-text>
        <v-card-actions class="pt-4 border-t border-gray-100 bg-white">
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            depressed
            @click="previewDialog = false"
            class="normal-case rounded-lg px-8"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Create/Edit Dialog - DESIGN UPDATED TO MATCH AddTemplate.vue -->
    <v-dialog v-model="dialog" persistent width="1200px">
      <v-card flat class="d-flex flex-column p-6" style="height: 90vh">
        <!-- Header -->
        <v-card-title
          class="py-4 px-6 d-flex justify-space-between border-b border-gray-200 top-header flex-shrink-0"
        >
          <h3 class="form-title">
            {{ isEditing ? 'Edit Letter Template' : 'Create New Letter Template' }}
          </h3>
          <v-btn icon @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <!-- Scrollable Content Area -->
        <div class="overflow-y-auto flex-grow-1 p-6 bg-gray-50 p-2">
          <v-row>
            <v-col cols="12">
              <v-card outlined class="rounded-lg pa-4 bg-white border-gray-200">
                <v-row>
                  <v-col cols="12">
                    <v-text-field
                      v-model="form.name"
                      label="Template Name*"
                      placeholder="e.g. welcome_letter"
                      outlined
                      dense
                      required
                      class="rounded-lg"
                      hide-details
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <v-select
                      v-model="form.type"
                      :items="['EMAIL', 'WHATSAPP', 'SMS', 'ATTACHMENT']"
                      label="Template Type*"
                      outlined
                      dense
                      class="rounded-lg"
                      hide-details
                    ></v-select>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" v-if="form.type === 'ATTACHMENT'">
                    <v-select
                      v-model="form.attachmentType"
                      :items="['PDF', 'EXCEL', 'WORD']"
                      label="Attachment Formats*"
                      chips
                      small
                      outlined
                      dense
                      class="rounded-lg"
                      hide-details
                    ></v-select>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>

            <v-col cols="12">
              <v-card outlined class="rounded-lg pa-4 bg-white border-gray-200 mt-2">
                <div class="d-flex justify-space-between align-center mb-0">
                  <h4 class="text-subtitle-1 font-bold text-gray-800">Available Parameters</h4>
                </div>

                <!-- Module Selection Dropdown -->
                <div class="mb-4">
                  <v-select
                    v-model="selectedModule"
                    :items="uniqueModules"
                    label="Filter by Module"
                    outlined
                    dense
                    hide-details
                    class="rounded-lg mt-2"
                    prepend-inner-icon="mdi-filter-variant"
                    item-text="text"
                    item-value="value"
                  ></v-select>
                </div>

                <div v-if="!selectedModule" class="text-sm text-gray-400 italic py-2">
                  Please select a module to see available parameters...
                </div>

                <!-- Global/Common Parameters section -->
                <div v-if="globalSpecificParameters.length > 0" class="mb-4">
                  <div
                    class="text-xs font-bold text-blue-600 uppercase tracking-wider mb-2 d-flex align-center"
                  >
                    <v-icon x-small color="blue" class="mr-1">mdi-earth</v-icon>
                    Global Parameters:
                  </div>
                  <div
                    class="parameter-grid-container pa-2 border-2 border-blue-100 rounded-lg bg-blue-50/20 overflow-y-auto"
                    style="max-height: 120px"
                  >
                    <div class="d-flex flex-wrap gap-2">
                      <v-chip
                        v-for="opt in globalSpecificParameters"
                        :key="opt.value"
                        x-small
                        outlined
                        color="blue darken-3"
                        class="ma-1 parameter-clickable-chip bg-white"
                        @click="insertParameter(opt.value)"
                      >
                        {{ opt.text }}
                      </v-chip>
                    </div>
                  </div>
                </div>

                <div class="mb-4">
                  <v-autocomplete
                    :items="parameterOptions"
                    label="Search & Select Parameter"
                    outlined
                    dense
                    hide-details
                    class="rounded-lg mt-2"
                    prepend-inner-icon="mdi-magnify"
                    @change="
                      (val) => {
                        if (val) {
                          insertParameter(val)
                          $nextTick(() => {
                            selectedParam = null
                          })
                        }
                      }
                    "
                    v-model="selectedParam"
                    :disabled="!selectedModule"
                    placeholder="Search for a parameter..."
                    clearable
                  ></v-autocomplete>

                  <!-- Selected Parameters Chips -->
                  <div v-if="selectedParameters.length > 0" class="mt-3 flex flex-wrap gap-2">
                    <v-chip
                      v-for="(param, idx) in selectedParameters"
                      :key="`sel-param-${idx}`"
                      close
                      size="small"
                      @click:close="removeSelectedParam(idx)"
                      color="blue-grey lighten-5"
                      class="text-blue-grey text-xs font-medium"
                    >
                      {{ param }}
                    </v-chip>
                  </div>
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" v-if="form.type === 'EMAIL' || form.type === 'WHATSAPP'">
              <v-text-field
                v-model="form.subject"
                label="Subject"
                outlined
                dense
                hide-details
                class="rounded-lg"
                prepend-inner-icon="mdi-format-title"
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-card outlined class="rounded-lg pa-4 bg-white border-gray-200 mt-2">
                <label class="text-subtitle-1 font-bold text-gray-800 mb-3 d-block"
                  >Body Content*</label
                >
                <div
                  v-if="dialog"
                  class="editor-wrapper border rounded-lg overflow-hidden"
                  style="min-height: 400px"
                >
                  <VueEditor
                    ref="letterEditor"
                    :key="isEditing ? selectedLetter?._id || 'edit' : 'new'"
                    v-model="form.content"
                    :editor-toolbar="customToolbar"
                    placeholder="Design your template content here..."
                    class="quill-editor"
                  />
                </div>
              </v-card>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" v-if="form.type === 'EMAIL'">
              <v-autocomplete
                v-model="form.attachments"
                :items="availableAttachments"
                label="Add Attachments"
                multiple
                chips
                outlined
                dense
                class="rounded-lg"
                hide-details
                prepend-inner-icon="mdi-paperclip"
              ></v-autocomplete>
            </v-col>
          </v-row>
        </div>

        <!-- Footer -->
        <div
          class="d-flex justify-end p-4 border-t border-gray-200 flex-shrink-0 bg-white shadow-top"
        >
          <v-btn
            variant="outlined"
            color="primary"
            @click="dialog = false"
            class="mr-3 normal-case rounded-lg px-6"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            @click="saveLetter"
            :loading="saving"
            class="normal-case bg-blue-600 text-white rounded-lg px-8 shadow-sm"
          >
            <v-icon left v-if="!saving">mdi-content-save</v-icon>
            {{ isEditing ? 'Update Template' : 'Save Template' }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card class="rounded-xl overflow-hidden">
        <v-card-title class="bg-red-50 text-red-800 py-4 px-6 font-bold">
          Delete Template?
        </v-card-title>
        <v-card-text class="pa-6 text-gray-600">
          Are you sure you want to delete
          <span class="font-bold text-gray-800"
            >"{{ selectedLetter?.name || selectedLetter?.letterName }}"</span
          >? This action cannot be undone and any notifications linked to this letter will fail to
          attach it.
        </v-card-text>
        <v-card-actions class="pa-4 bg-gray-50">
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false" class="normal-case">Cancel</v-btn>
          <v-btn color="error" depressed @click="deleteLetter" class="normal-case rounded-lg px-6">
            Confirm Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { VueEditor } from 'vue2-editor'
import { getNotificationTriggerOptions } from '@/utils/workforce/constants'

export default {
  name: 'LetterTemplates',
  components: { VueEditor },
  data() {
    return {
      letters: [],
      globalParameters: [],
      selectedModule: '',
      selectedParam: null,
      selectedParameters: [],
      search: '',
      loading: false,
      saving: false,
      dialog: false,
      deleteDialog: false,
      previewDialog: false,
      isEditing: false,
      selectedLetter: null,
      form: {
        templateCode: '',
        name: '',
        type: 'EMAIL',
        subject: '',
        content: '',
        module: '',
        attachmentType: '',
        attachments: []
      },
      customToolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote', 'code-block'],
        [{ header: 1 }, { header: 2 }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ script: 'sub' }, { script: 'super' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ direction: 'rtl' }],
        [{ size: ['small', false, 'large', 'huge'] }],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ color: [] }, { background: [] }],
        [{ font: [] }],
        [{ align: [] }],
        ['clean'],
        ['link', 'image', 'video']
      ]
    }
  },
  watch: {
    'form.content'(newVal) {
      this.extractParametersFromContent(newVal)
    },
    selectedModule(newVal) {
      this.form.module = newVal
    }
  },
  computed: {
    availableAttachments() {
      return (this.letters || [])
        .filter((l) => l.type === 'ATTACHMENT')
        .map((l) => ({
          text: l.name,
          value: l._id
        }))
    },
    filteredLetters() {
      if (!this.search) return this.letters
      const s = this.search.toLowerCase()
      return this.letters.filter(
        (l) =>
          (l.name && l.name.toLowerCase().includes(s)) ||
          (l.letterName && l.letterName.toLowerCase().includes(s)) ||
          (l.content && l.content.toLowerCase().includes(s)) ||
          (l.letterContent && l.letterContent.toLowerCase().includes(s))
      )
    },
    parameterOptions() {
      if (!this.selectedModule) return []
      let params = this.globalParameters.filter((p) => p.module !== 'Global')
      params = params.filter((p) => p.module === this.selectedModule)

      if (params.length > 0) {
        return params.map((p) => ({
          text: p.parameterDescription || p.parameterKey,
          value: p.parameterKey
        }))
      }
      return []
    },
    uniqueModules() {
      const modules = new Set(
        this.globalParameters.filter((p) => p.module !== 'Global').map((p) => p.module)
      )
      return Array.from(modules)
        .sort()
        .map((m) => ({
          text: m.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
          value: m
        }))
    },
    globalSpecificParameters() {
      return this.globalParameters
        .filter((p) => p.module === 'Global')
        .map((p) => ({
          text: p.parameterDescription || p.parameterKey,
          value: p.parameterKey
        }))
    }
  },
  async mounted() {
    await Promise.all([this.fetchLetters(), this.fetchParameters()])
  },
  methods: {
    async fetchLetters() {
      try {
        this.loading = true
        const response = await this.$axios.get('/letter-templates')
        this.letters = response.data?.data || response.data || []
      } catch (error) {
        console.error('Error fetching letters:', error)
        this.$toast.error('Failed to fetch letter templates')
      } finally {
        this.loading = false
      }
    },
    async fetchParameters() {
      try {
        const response = await this.$axios.get('/template-params-mapping')
        const rawData = response.data?.data || response.data || {}

        // Transform the nested mapping into a flat array format for the UI
        const flattenedParams = []

        Object.entries(rawData).forEach(([moduleKey, moduleData]) => {
          const parameters = moduleData.dataParameters || {}

          Object.entries(parameters).forEach(([subModuleKey, params]) => {
            Object.entries(params).forEach(([paramKey, paramData]) => {
              flattenedParams.push({
                module: moduleKey, // Use snake_case key (e.g. loan_management)
                subModule: subModuleKey,
                parameterKey: paramKey,
                parameterDescription: paramData.displayName || paramKey,
                path: paramData.path
              })
            })
          })
        })

        this.globalParameters = flattenedParams
      } catch (error) {
        console.error('Error fetching template parameters:', error)
      }
    },
    openPreview(letter) {
      this.selectedLetter = letter
      this.previewDialog = true
    },
    previewLetter(letter) {
      this.selectedLetter = letter
      this.previewDialog = true
    },
    insertParameter(param) {
      if (param) {
        const variableText = `{{${param}}}`

        // Get Quill instance from ref
        const quill = this.$refs.letterEditor?.quill
        if (quill) {
          const range = quill.getSelection(true)
          if (range) {
            // Replace selection if any, or just insert at index
            if (range.length > 0) {
              quill.deleteText(range.index, range.length)
            }
            quill.insertText(range.index, variableText, 'user')
            // Move cursor to end of inserted text
            quill.setSelection(range.index + variableText.length)
          } else {
            this.form.content += variableText
          }
        } else {
          // Fallback if quill is not ready
          this.form.content += variableText
        }

        this.$toast.info(`Inserted {{${param}}}`)
      }
    },
    removeSelectedParam(index) {
      const param = this.selectedParameters[index]
      if (param) {
        const variableText = `{{${param}}}`
        // Remove all instances of the parameter from the content
        if (this.form.content) {
          this.form.content = this.form.content.split(variableText).join('')
        }
      }
      this.selectedParameters.splice(index, 1)
    },
    openCreateDialog() {
      this.isEditing = false
      this.selectedLetter = null
      this.form = {
        templateCode: '',
        name: '',
        type: 'EMAIL',
        subject: '',
        content: '',
        module: 'All',
        attachmentType: '',
        attachments: []
      }
      this.dialog = true
    },
    editLetter(letter) {
      this.isEditing = true
      this.selectedLetter = letter
      // Set the active filter to match the template's module
      this.selectedModule = letter.module || ''

      this.form = {
        templateCode: letter.templateCode || '',
        name: letter.name || letter.letterName || '',
        type: letter.type || 'EMAIL',
        subject: letter.subject || '',
        content: letter.content || letter.letterContent || '',
        module: letter.module || '',
        attachmentType: letter.attachmentType || '',
        attachments: letter.attachments || []
      }

      // Auto-extract parameters from content for the chips
      this.extractParametersFromContent(this.form.content)

      this.dialog = true
    },
    extractParametersFromContent(content) {
      if (!content) {
        this.selectedParameters = []
        return
      }

      // Support both {{param}} and #param#
      const curlyRegex = /\{\{([^}]+)\}\}/g
      const hashRegex = /#([a-zA-Z0-9_]+)#/g

      const found = []
      let match

      // Extract {{...}}
      while ((match = curlyRegex.exec(content)) !== null) {
        if (!found.includes(match[1])) found.push(match[1])
      }

      // Extract #...#
      while ((match = hashRegex.exec(content)) !== null) {
        if (!found.includes(match[1])) found.push(match[1])
      }

      this.selectedParameters = found
    },
    getTypeStyle(type) {
      switch (type) {
        case 'SMS':
          return { bg: 'amber lighten-5', color: 'amber darken-4', icon: 'mdi-message-processing' }
        case 'WHATSAPP':
          return { bg: 'green lighten-5', color: 'green darken-4', icon: 'mdi-whatsapp' }
        case 'EMAIL':
          return { bg: 'blue lighten-5', color: 'blue darken-4', icon: 'mdi-email-outline' }
        case 'ATTACHMENT':
          return { bg: 'purple lighten-5', color: 'purple darken-4', icon: 'mdi-paperclip' }
        default:
          return { bg: 'grey lighten-4', color: 'grey darken-3', icon: 'mdi-file-outline' }
      }
    },
    async saveLetter() {
      if (!this.form.name) {
        this.$toast.error('Template name is required')
        return
      }

      // Ensure module is snake_case (backup check)
      if (this.selectedModule && this.selectedModule !== 'Global') {
        this.form.module = this.selectedModule.toLowerCase().replace(/\s+/g, '_')
      } else if (this.selectedModule === 'Global') {
        this.form.module = 'global'
      }

      try {
        this.saving = true
        if (this.isEditing) {
          await this.$axios.patch(`/letter-templates/${this.selectedLetter._id}`, this.form)
        } else {
          await this.$axios.post('/letter-templates', this.form)
        }

        this.$toast.success('Template saved successfully')
        this.dialog = false
        await this.fetchLetters()
      } catch (error) {
        console.error('Error saving letter:', error)
        const msg = error.response?.data?.message || 'Failed to save letter template'
        this.$toast.error(msg)
      } finally {
        this.saving = false
      }
    },
    confirmDelete(letter) {
      this.selectedLetter = letter
      this.deleteDialog = true
    },
    async deleteLetter() {
      try {
        await this.$axios.delete(`/letter-templates/${this.selectedLetter._id}`)
        this.$toast.success('Letter template deleted')
        this.deleteDialog = false
        await this.fetchLetters()
      } catch (error) {
        console.error('Error deleting letter:', error)
        this.$toast.error('Failed to delete letter template')
      }
    },
    formatDate(dateStr, dateOnly = false) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      if (dateOnly) return date.toLocaleDateString('en-IN')

      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    formatModuleName(name) {
      if (!name || name === 'All') return name
      return name.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
    }
  }
}
</script>

<style scoped>
.letter-templates-container {
  padding: 24px;
  background-color: #f9fafb;
  min-height: 80vh;
}

.letter-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background-color: white;
  border: 1px solid #d1d5db;
}

/* .letter-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 15px 30px -10px rgba(0, 0, 0, 0.1) !important;
} */

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.form-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #21427d;
  margin: 0;
}

.top-header {
  position: sticky;
  top: 0;
  background-color: #fff;
  z-index: 50;
}

.shadow-top {
  box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.05);
}

.param-chip {
  transition: all 0.2s ease;
  border: 1px solid #d1d5db;
}

.param-chip:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.quill-editor {
  height: 400px;
}

.editor-wrapper :deep(.ql-container) {
  height: 350px !important;
}

.editor-wrapper :deep(.ql-editor) {
  min-height: 350px !important;
}

.editor-wrapper {
  background: white;
}

/* Customizing scrollbar for scrollable text area */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}
.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: #d1d5db;
  border-radius: 10px;
}
</style>
